import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiService, BlockedUser } from '../services/api';

interface UseBlockedUsersOptions {
  apiService: ApiService;
  getToken: () => Promise<string | null> | string | null;
  /** If false, the hook does not fetch on mount. Consumer must call refresh() manually. */
  autoFetch?: boolean;
}

interface UseBlockedUsersReturn {
  blockedIds: Set<string>;
  blockedUsers: BlockedUser[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  block: (userId: string, reason?: string) => Promise<void>;
  unblock: (userId: string) => Promise<void>;
  isBlocked: (userId: string) => boolean;
}

/**
 * Shared hook for both mobile and web. Keeps the caller's blocked-user set in memory
 * for instant feed filtering; block()/unblock() update the local set immediately
 * after the server call succeeds so UI updates without a refetch.
 */
export const useBlockedUsers = ({
  apiService,
  getToken,
  autoFetch = true,
}: UseBlockedUsersOptions): UseBlockedUsersReturn => {
  const [blockedUsers, setBlockedUsers] = useState<BlockedUser[]>([]);
  const [blockedIds, setBlockedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const resolveToken = useCallback(async (): Promise<string | null> => {
    const result = getToken();
    return result instanceof Promise ? await result : result;
  }, [getToken]);

  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setError(null);
    try {
      const token = await resolveToken();
      if (!token) {
        setBlockedUsers([]);
        setBlockedIds(new Set());
        return;
      }
      const res = await apiService.getBlockedUsers(token);
      const list = res?.data?.blocked ?? [];
      setBlockedUsers(list);
      setBlockedIds(new Set(list.map((u) => u.id)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load blocked users');
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  }, [apiService, resolveToken]);

  const block = useCallback(
    async (userId: string, reason?: string) => {
      const token = await resolveToken();
      if (!token) throw new Error('Not authenticated');
      await apiService.blockUser(userId, token, reason);
      setBlockedIds((prev) => {
        const next = new Set(prev);
        next.add(userId);
        return next;
      });
      setBlockedUsers((prev) =>
        prev.some((u) => u.id === userId) ? prev : [...prev, { id: userId }]
      );
    },
    [apiService, resolveToken]
  );

  const unblock = useCallback(
    async (userId: string) => {
      const token = await resolveToken();
      if (!token) throw new Error('Not authenticated');
      await apiService.unblockUser(userId, token);
      setBlockedIds((prev) => {
        if (!prev.has(userId)) return prev;
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
      setBlockedUsers((prev) => prev.filter((u) => u.id !== userId));
    },
    [apiService, resolveToken]
  );

  const isBlocked = useCallback((userId: string) => blockedIds.has(userId), [blockedIds]);

  useEffect(() => {
    if (autoFetch) {
      void refresh();
    }
  }, [autoFetch, refresh]);

  return { blockedIds, blockedUsers, loading, error, refresh, block, unblock, isBlocked };
};
